import type { Edge, Node } from '../types';

/** v11: `deletable` defaults to true. */
const isDeletable = (item: { deletable?: boolean }): boolean => item.deletable ?? true;

/** Edges that touch any of `nodes`. */
export const getConnectedEdges = <TEdge extends Edge>(nodes: readonly Node[], edges: readonly TEdge[]): TEdge[] => {
  const ids = new Set(nodes.map((node) => node.id));
  return edges.filter((edge) => ids.has(edge.source) || ids.has(edge.target));
};

/**
 * What the delete key removes (v11 `deleteElements`): selected deletable nodes, and the selected deletable edges plus
 * every deletable edge connected to a removed node — selected edges first, no duplicates.
 */
export const getElementsToDelete = <TNode extends Node, TEdge extends Edge>(
  nodes: readonly TNode[],
  edges: readonly TEdge[],
): { nodes: TNode[]; edges: TEdge[] } => {
  const nodesToRemove = nodes.filter((node) => node.selected && isDeletable(node));
  const deletableEdges = edges.filter(isDeletable);
  const candidates = [...deletableEdges.filter((edge) => edge.selected), ...getConnectedEdges(nodesToRemove, deletableEdges)];
  const edgesToRemove = candidates.filter((edge, index) => candidates.findIndex((other) => other.id === edge.id) === index);
  return { nodes: nodesToRemove, edges: edgesToRemove };
};
